import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, UserPlus, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import TeamAvatar from './TeamAvatar'
import { useTeamStore } from '../store/useTeamStore'
import { countGraphemes, teamErrorText } from '../utils/teamFormat'
import { hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

const TEXT_MAX = 300
const APPLY_TEXT_STORAGE_KEY = 'skinoz_team_apply_text'

/**
 * Подача заявки в банду. Текст необязателен, до 300 графем и не редактируется:
 * потом можно только отозвать и подать заново, не чаще раза в 5 минут.
 * Отправленный текст сохраняется в localStorage и подставляется при следующем
 * открытии модалки, чтобы не вводить его заново.
 */
const TeamApplyModal = ({ team, isOpen, onClose, onApplied }) => {
  const { t } = useTranslation()
  const { apply, isMutating } = useTeamStore()
  const [text, setText] = useState(() => localStorage.getItem(APPLY_TEXT_STORAGE_KEY) || '')

  // При каждом открытии подставляем последний отправленный текст
  useEffect(() => {
    if (isOpen) setText(localStorage.getItem(APPLY_TEXT_STORAGE_KEY) || '')
  }, [isOpen])

  if (!isOpen || !team) return null

  const length = countGraphemes(text)
  const isTooLong = length > TEXT_MAX

  const handleApply = async () => {
    if (isTooLong || isMutating) return

    const res = await apply(team.id, text.trim())
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-apply')
      toast.success(t('team.applySent', 'Заявка отправлена'))
      try {
        localStorage.setItem(APPLY_TEXT_STORAGE_KEY, text.trim())
      } catch (e) {
        // ignore
      }
      onApplied?.()
      return
    }
    if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-black shadow-2xl">
        <button
          onClick={onClose}
          disabled={isMutating}
          className="absolute right-4 top-4 p-2 text-text-secondary transition-colors hover:text-white disabled:opacity-50"
          aria-label={t('common.close', 'Закрыть')}
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <div className="mb-4 flex items-center gap-3">
            <TeamAvatar team={team} size={48} />
            <div className="min-w-0">
              <h3 className="truncate text-lg font-bold not-italic text-white">{team.name}</h3>
              <p className="text-xs text-text-secondary tabular-nums">
                {team.members_count}/{team.max_members}
              </p>
            </div>
          </div>

          <p className="mb-4 text-sm text-text-secondary">
            {t('team.applyDescription', 'Расскажи о себе — это увидят лидеры банды. Текст можно не писать.')}
          </p>

          <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
            <span>{t('team.applyText', 'Сообщение')}</span>
            <span className={`tabular-nums ${isTooLong ? 'text-red-400' : ''}`}>
              {length}/{TEXT_MAX}
            </span>
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder={t('team.applyPlaceholder', 'Например: Играю каждый день, люблю режим X50, ищу активную банду. Профиль открыт.')}
            className="w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
          />

          <button
            onClick={handleApply}
            disabled={isTooLong || isMutating}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary p-4 font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {isMutating ? <Loader2 size={20} className="animate-spin" /> : <UserPlus size={18} />}
            {t('team.applySubmit', 'Отправить заявку')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TeamApplyModal
